import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useImmer } from 'use-immer';
import PropTypes from 'prop-types';
import RestHelper from 'utils/RestHelper';
import {
  Form,
  Modal,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
  Spin,
  Button,
  notification,
  AutoComplete,
  Divider,
  Radio,
  Checkbox,
  Space,
  Tooltip,
  Popconfirm,
  Alert,
} from 'antd';
import { connect, useSelector, useDispatch } from 'react-redux';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import { red } from '@ant-design/colors';
import isString from 'lodash/lang/isString';
import { isNotNullish, isNullish } from 'utils/nullish';
import { useCrashsightLocalStorage } from 'utils/useCrashSightLocalStorage';
import uniq from 'lodash/array/uniq';
import { getIssueDetailUrlPrefix } from 'utils/helper';
import { UserTypeEnum } from 'utils/constants/user-type-enum';
import { TapdTitleTemplateUtil } from 'components/exception/issue/TapdTitleTemplateEditor';
import { isZh, ze } from 'utils/zhEn';
import cloneDeep from 'lodash/lang/cloneDeep';
import scss from './TapdModal.scss';
import TapdEditBugCustomFieldsModal, { TapdEditBugCustomFieldsUtil } from 'components/exception/issue/TapdEditBugCustomFieldsModal';
import TapdFieldFormItem from 'components/exception/issue/TapdFieldFormItem';
import TapdFieldFormItemErrorBoundary from 'components/exception/issue/TapdFieldFormItemErrorBoundary';
import { useTranslation } from 'react-i18next';
import TapdModelEditTitleTemplateModel from 'components/exception/issue/TapdModelEditTitleTemplateModel';
import isArray from 'lodash/lang/isArray';
import { EnvUtil } from 'utils/env-util';
import { Base64 } from 'utils/base64';
import { CrashUtil } from 'utils/api/crash';
import { getReportCategoryByExceptionTypeInt } from 'utils/constants/exception-type-int';
import { selectHasSpecifiedPermissionInCurrentApp } from 'utils/selectors/selectors';
import { RolePermission } from 'utils/constants/role-permission';
import { CrashAttachmentUtil } from 'utils/crash-attachment-util';
import { CsSelect } from 'components/antd-extension/CsSelect';
import { ServerAppSettings } from 'utils/server-app-settings';
import { ix } from 'utils/i18n-with-context';
import { IssueAiSolutionItemType, IssueAiSolutionUtil } from 'components/exception/issue/IssueHead/IssueAiSolution';
import gfm from 'remark-gfm';
import ReactMarkdown from 'react-markdown';
import { AiIssueAnalysisUtil } from 'components/top/AiDrawer/ai-issue-analysis';
import { TapdUtil } from 'utils/tapd-util';
import { formatTapdErrorMessage, shouldShowTapdConfigLink, getTapdErrorFromReject } from 'utils/tapd-error-util';
import {
  deleteRemovedTemplateKeys,
  applyTemplateDefaults,
  removedKeys,
  antdClearAndSet,
  mergeFormChange,
  FEATURE_ANALYSIS_SKIP_TEMPLATE_FIELDS,
  TAPD_MODAL_RESERVED_FORM_KEYS,
  withAlwaysRequiredTemplateFields,
  resolveInitialTemplateId,
  TAPD_TEMPLATE_ALWAYS_REQUIRED_REPORTER,
} from 'utils/tapd-template-form-util';
import TapdDefaultTemplateModal from 'components/exception/issue/TapdDefaultTemplateModal';
import JiraTeamOptionsModal from 'components/exception/issue/JiraTeamOptionsModal';
import { isJiraTeamField, parseTeamOptions, toTeamSelectOptions } from 'utils/jira-team-options';
import { upsertServerAppSettings } from 'reducers/app/appActions';

const { TextArea } = Input;

// 驼峰式字段名（代码中使用）到下划线式字段名（API返回）的映射
// 对齐 TapdEditBugCustomFieldsModal 中的定义
const CAMEL_TO_SNAKE_CASE_FIELD_MAP = {
  severity: 'severity',
  reporter: 'reporter',
  versionReport: 'version_report',
  iterationId: 'iteration_id',
  module: 'module',
  releaseId: 'release_id',
  priority: 'priority',
  currentOwner: 'current_owner',
  baseline_find: 'baseline_find',
  os: 'os',
  frequency: 'frequency',
};

const TAPD_SEVERITY_LIST = [
  { value: 'fatal', label: ze('致命', 'Fatal') },
  { value: 'serious', label: ze('严重', 'Serious') },
  { value: 'normal', label: ze('一般', 'Normal') },
  { value: 'prompt', label: ze('提示', 'Prompt') },
  { value: 'advice', label: ze('建议', 'Advice') },
];

const JIRA_PRIORITY_LIST = [
  { value: '1', label: ze('最高', 'Highest') },
  { value: '2', label: ze('高', 'High') },
  { value: '3', label: ze('中等', 'Medium') },
  { value: '4', label: ze('低', 'Low') },
  { value: '5', label: ze('最低', 'Lowest') },
];

const MAX_TITLE_LENGTH = 200;

const TAB_CREATE_BUG = 'TAB_CREATE_BUG';
const TAB_BIND_BUG = 'TAB_BIND_BUG';

/**
 * 向一个array-like queue的头部添加一个元素（string）。
 * 返回新的queue。原来的queue内容不受影响。
 * 如果queue长度超过指定上限，则从末尾弹出多余元素
 * @param {string[]} queue
 * @param {string} pushValue
 * @param {number} queueLengthLimit
 * @param {boolean} removeDuplicate
 */
function leftPushValueToLimitedLengthQueue(queue, pushValue, queueLengthLimit, removeDuplicate) {
  let res = [pushValue, ...queue];
  if (removeDuplicate) {
    res = uniq(res);
  }
  if (res.length > queueLengthLimit) {
    res = res.filter((_, i) => i < queueLengthLimit);
  }
  return res;
}

// tapd相关拉取接口10分钟缓存
const TAPD_API_CACHE_INTERVAL_MILLIS = 10 * 60 * 1000;

const TapdModal = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reduxState, issueInfoList, modalProps } = props;
  const modalVisible = modalProps.visible;
  const currentApp = reduxState.app.get('current').toJS();
  const { appId, platformId } = currentApp;
  const currentUser = reduxState.user.get('current').toJS();
  const { nickname: userNickname } = currentUser;

  const hasViewPersonalDataPermission = useSelector(state => selectHasSpecifiedPermissionInCurrentApp(state, RolePermission.VIEW_PERSONAL_DATA));
  const hasAdminPermission = useSelector(state => selectHasSpecifiedPermissionInCurrentApp(state, RolePermission.EDIT));

  const settings = reduxState.app.get('appIdToWebAppSettings')[appId];
  const titleTemplateFromSettings = (settings || {})[TapdTitleTemplateUtil.getSettingKey()]
    || TapdTitleTemplateUtil.makeDefaultValue(platformId);

  const bugTrackingSimplifiedInfo = useMemo(() => reduxState.app.get('appIdToBugTrackingSimplifiedInfo')?.[appId],
    [reduxState.app, appId]);
  const { tapdWorkspaceId, jiraProjectId } = bugTrackingSimplifiedInfo || {};
  const workspaceId = tapdWorkspaceId;
  const workspaceIdLoading = isNullish(bugTrackingSimplifiedInfo);

  const enabledBugCustomFieldsFromSettings = (settings || {})[TapdEditBugCustomFieldsUtil.getSettingKey()] || [];

  const serverAppSettings = reduxState.app.get('appIdToServerAppSettings')[appId] || {};
  const tapdFieldList = serverAppSettings[ServerAppSettings.keys.tapdFieldList];
  /** 项目级「默认提单模板」配置（手动提单）。undefined = 未配置，走TAPD侧默认模板。 --story=1020421727138399301 */
  const tapdBugDefaultTemplateSetting = serverAppSettings[ServerAppSettings.keys.tapdBugDefaultTemplate];
  /** 页面上Jira提单的时候，字段的默认值。是一个字典，对应的java后端类型是Map<String, Object>，key是字段的key（不是label），value是字段的默认值 */
  const jiraFieldDefaultValue = serverAppSettings[ServerAppSettings.keys.jiraFieldDefaultValue] || {};
  /** Jira Team 字段的可选项：项目级 CSV 文本（见 JiraTeamOptionsModal）。--story=1020421727138529159 */
  const jiraTeamOptionsText = serverAppSettings[ServerAppSettings.keys.jiraTeamOptions] || '';
  /** 解析后的 Team 选项；为空表示项目未配置，此时 Team 字段退化为手填 Team UUID */
  const jiraTeamOptions = useMemo(() => parseTeamOptions(jiraTeamOptionsText).items, [jiraTeamOptionsText]);
  const enabledFieldList = TapdEditBugCustomFieldsUtil.getEnabledFieldList(tapdFieldList, enabledBugCustomFieldsFromSettings);
  const enabledBuiltInFieldList = enabledFieldList.filter(x => !TapdEditBugCustomFieldsUtil.checkIsCustomField(x.field));
  const enabledCustomFieldList = enabledFieldList.filter(x => TapdEditBugCustomFieldsUtil.checkIsCustomField(x.field));
  const enabledBuiltInFieldKeyToInfo = Object.assign(Object.create(null), ...enabledBuiltInFieldList.map(x => ({ [x.field]: x })));

  // 默认bug创建人，直接取昵称
  const defaultReporter = userNickname;

  const isMultipleIssues = issueInfoList && issueInfoList.length > 1;

  const redirectToTapdConfigPage = () => {
    window.open(`/crash-reporting/preferences/tapd/${appId}?pid=${platformId}`, '_blank');
  };

  const [jiraIdLoading, setJiraIdLoading] = useState(true);
  const [bondedJiraProject, setBondedJiraProject] = useState(null)
  const [jiraUserList, setJiraUserList] = useState([]);
  const [jiraTypesList, setJiraTypesList] = useState([])
  const [usersLoading, setUsersLoading] = useState(true)
  const [typesLoading, setTypesLoading] = useState(true)

  const [tabType, setTabType] = useState(TAB_CREATE_BUG);

  const [issueInfo, updateIssueInfo] = useImmer({});

  const [form, updateForm] = useImmer({});

  const [formUpdateTrigger, setFormUpdateTrigger] = useState(0);

  const [formSubmitTrigger, setFormSubmitTrigger] = useState(0);

  const [loading, setLoading] = useState(false);

  const [formInstance] = Form.useForm();

  const [iterationList, setIterationList] = useState([]);

  const [versionList, setVersionList] = useState([]);

  const [releaseList, setReleaseList] = useState([]);

  const [tapdModuleList, setTapdModuleList] = useState([]);

  const [priorityList, setPriorityList] = useState([]);

  const [workspaceUserOptions, setWorkspaceUserOptions] = useState([]);

  const [bugFieldValueToOptions, setBugFieldValueToOptions] = useState({});
  const [bugCustomFields, setBugCustomFields] = useState([]);
  const [bugFieldToInfo, setBugFieldToInfo] = useState({});
  const bugFieldInfoList = useMemo(() => Object.values(bugFieldToInfo), [bugFieldToInfo]);

  const [tapdFieldsInfoLastFetchedMillis, setTapdFieldsInfoLastFetchedMillis] = useState(0);
  const [tapdFieldsError, setTapdFieldsError] = useState(null);
  const [workspaceUsersLastFetchedMillis, setWorkspaceUsersLastFetchedMillis] = useState(0);

  const [attachmentFilenameOptions, setAttachmentFilenameOptions] = useState([]);
  const [attachmentOptionsLoaded, setAttachmentOptionsLoaded] = useState(false);
  const [attachmentOptionsLoading, setAttachmentOptionsLoading] = useState(false);

  const [tapdTitleTemplateModalVisible, setTapdTitleTemplateModalVisible] = useState(false);
  const [tapdEditBugCustomFieldsModalVisible, setTapdEditBugCustomFieldsModalVisible] = useState(false);
  const [tapdDefaultTemplateModalVisible, setTapdDefaultTemplateModalVisible] = useState(false);
  // 模板列表是异步请求，返回时弹窗可能已经关了，用它做 stale 守卫
  const modalVisibleRef = useRef(modalVisible);
  useEffect(() => {
    modalVisibleRef.current = modalVisible;
  }, [modalVisible]);

  // TAPD缺陷模板：selectedTemplateId为''表示不使用模板，沿用tapdFieldList固定字段方案
  const [tapdTemplateList, setTapdTemplateList] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  // 项目配置的「默认提单模板」解析出来的初始模板id，仅用于在下拉里标注「（默认）」
  const [defaultTemplateIdFromSetting, setDefaultTemplateIdFromSetting] = useState('');
  // 选了模板后，该模板的字段配置（required/默认值/排序）。未选模板时为null。
  const [templateFieldConfigList, setTemplateFieldConfigList] = useState(null);
  const [templateFieldsLoading, setTemplateFieldsLoading] = useState(false);
  const previousTemplateFieldNamesRef = useRef(null);
  /** 上一次 reconcile 时的模板 id：用来识别「用户主动切了模板下拉」（即使字段集合没变也要重填默认值）。
   *  --story=1020421727138452632 */
  const previousTemplateIdForReconcileRef = useRef('');
  /** 当前 templateFieldConfigList 属于哪个模板 id。新模板的字段配置没到之前，
   *  reconcile 必须等，否则会拿着上一个模板的配置跑一轮、把新模板的默认值挡在外面。
   *  --story=1020421727138452632 */
  const templateFieldConfigOwnerRef = useRef('');
  /** 由「模板默认值自动填进去」的字段。切模板时这些值要跟着换（否则两个模板字段高度重合时，
   *  切过去看到的还是上一个模板的默认值）；用户手填过的字段不在里面，不会被清掉。
   *  --story=1020421727138452632 */
  const autoFilledKeysRef = useRef(new Set());
  const ignoreTemplateReconcileRef = useRef(false);
  const [jiraBugTypeList, setJiraBugTypeList] = useState([]);
  const [jiraFieldsLoaded, setJiraFieldsLoaded] = useState(false);
  const [jiraTeamOptionsModalVisible, setJiraTeamOptionsModalVisible] = useState(false);
  /** 当前 issue type 下的 Jira Team 字段 key（如 customfield_10001）；没有 Team 字段时为空串 */
  const jiraTeamFieldKey = useMemo(() => {
    const field = (jiraBugTypeList || []).find(x => isJiraTeamField(x));
    return field ? field.key : '';
  }, [jiraBugTypeList]);
  // 王者需求：需要填充AI分析结论
  const aiSolutionMarkdownRef = useRef(); // 利用ReactMarkdown把markdown转换为HTML
  const [aiSolutionMarkdownText, setAiSolutionMarkdownText] = useState('');

  const [storage, updateStorage] = useCrashsightLocalStorage();

  // 提单人和处理人，需要在浏览器端记忆最近5次确认表单时填写的人员
  const { tapdReporterHistory } = storage;
  const reporterOptions = tapdReporterHistory.map(x => ({ name: x, value: x }));
  const MAX_REPORTER_HISTORY_LENGTH = 5;
  const pushReporter = (v) => {
    updateStorage((draft) => {
      draft.tapdReporterHistory = leftPushValueToLimitedLengthQueue(
        draft.tapdReporterHistory,
        v,
        MAX_REPORTER_HISTORY_LENGTH,
        true,
      );
    });
  };

  function makeInitialPartialForm(issueInfo, isMultipleIssues) {
    return {
      ...(!isMultipleIssues && makeAutoCompletedTapdBugInfoFromIssueInfo(issueInfo)),
      // 多个 issue 批量提单时，发现版本设为 null
      ...(isMultipleIssues && { version_report: null }),
      // 默认创建人
      ...(!bondedJiraProject ? { reporter: defaultReporter } : {}),

      isAlsoAddComment: EnvUtil.isChinaEnv(),
      includeAttachments: false,
      includeAiSolution: true,
      attachmentFilenameList: [],
    };
  }

  // 返回根据issueinfo自动构造的tapd bug info的部分
  function makeAutoCompletedTapdBugInfoFromIssueInfo(issueInfo) {
    const { exceptionName, keyStack, issueId, lastestUploadTime, imeiCount, count, issueExceptionType } = issueInfo;
    const detailInfo = issueInfo.detailInfo || {};
    const {
      retraceCrashDetail,
      crashId,
    } = detailInfo;

    const issueDetailUrlPrefix = getIssueDetailUrlPrefix(getReportCategoryByExceptionTypeInt(issueExceptionType));
    const crashIdUrlSuffix = crashId ? `/report-id/${crashId}` : '';
    const issueUrl = `${window.location.origin}/${issueDetailUrlPrefix}/${appId}/${issueId}${crashIdUrlSuffix}?pid=${platformId}`;

    const crashStackLines = `<pre>${String(retraceCrashDetail || '')}</pre>`;

    let description =
      `Issue ID: ${issueId}

${ze('异常详情地址', 'Web URL')}: <a href='${issueUrl}'>${issueUrl}</a>

${ze('最近上报时间', 'Last Reported Time')}: ${lastestUploadTime}

${t('REPORTDETAIL.异常进程')}#${t('REPORTDETAIL.异常线程')}: ${detailInfo.processName || ''}#${detailInfo.threadName || ''}

${t('REPORTDETAIL.expMessage')}：${detailInfo.expMessage || ''}

${t('issueCrashFilterKey.crashDetail')}:
${crashStackLines}

${t('issueCrashFilterKey.crashTime')}: ${detailInfo.crashTimeFormatted}
${t('issueCrashFilterKey.uploadTime')}: ${detailInfo.uploadTimeFormatted}
${t('REPORTDETAIL.apkName')}: ${detailInfo.bundleId}
${t('REPORTDETAIL.version')}: ${detailInfo.productVersion}
${t('REPORTDETAIL.hardware')}: ${detailInfo.hardware}
${t('REPORTDETAIL.osVersion')}: ${detailInfo.osVersion}
${t('CRASHDETAIL.totalReports')}：${count}
${ix(t('CRASHDETAIL.totalDevices'))}：${imeiCount}
`;

    const enrichedIssueInfo = { ...issueInfo, platformId };
    return {
      title: TapdTitleTemplateUtil.makeTitleFromTemplate(titleTemplateFromSettings, enrichedIssueInfo, detailInfo),
      description,
    };
  }

  function makeFinalTapdBugForSubmit(issueInfo, form, isMultipleIssues, extraDescription) {
    const tapdBugIdForUpdate = (issueInfo.bugs && issueInfo.bugs[0] && issueInfo.bugs[0].id) || null;
    let tapdBug;
    if (!isMultipleIssues) {
      tapdBug = {
        status: 'new',
        ...(tapdBugIdForUpdate && { id: tapdBugIdForUpdate }),
        ...form,
      };
    } else {
      tapdBug = {
        status: 'new',
        ...(tapdBugIdForUpdate && { id: tapdBugIdForUpdate }),
        ...form,
        ...makeAutoCompletedTapdBugInfoFromIssueInfo(issueInfo),
      };
    }
    if (extraDescription) {
      tapdBug = {
        ...tapdBug,
        description: tapdBug.description + extraDescription,
      };
    }

    // 提单模板：整次提单只能选一个模板，批量提单时对所有issue生效同一个模板
    if (selectedTemplateId) {
      tapdBug = {
        ...tapdBug,
        templateId: selectedTemplateId,
      };
    }

    // 对description进行处理，适配tapd那边的HTML结构
    let description = tapdBug.description;
    description = description.replaceAll('\n', '<br>');
    if (form?.includeAiSolution && aiSolutionMarkdownText) {
      if (aiSolutionMarkdownRef?.current) {
        const htmlString = aiSolutionMarkdownRef.current.innerHTML;
        description = description + '<br><hr>' + htmlString;
      } else {
        description = description + '<br><hr>' + aiSolutionMarkdownText;
      }
    }

    // title和description用base64编码
    tapdBug = {
      ...tapdBug,
      title: undefined,
      description: undefined,
      titleBase64: Base64.encodeAsString(tapdBug.title || ''),
      descriptionBase64: Base64.encodeAsString(description || ''),
    };
    return tapdBug;
  }

  // 拉取jiraId
  async function fetchJiraId() {
    setJiraIdLoading(true)
    const rsp = await RestHelper.mustPost('/redir/query/jira/getBindedJiraProject', { appId, platformId });
    const data = rsp.json.data;
    setBondedJiraProject(data);
    setJiraIdLoading(false);
  }

  function makeSingleFieldOptionsFromTapdFieldsInfoResult(data, field) {
    if (!data || !data[field]) {
      return [];
    }
    const optionMap = data[field].options || {};
    return Object.keys(optionMap).map(key => ({ value: key, label: optionMap[key] }));
  }

  function makeBugFieldValueToOptions(data) {
    if (!data) {
      return [];
    }
    const entries = Object.keys(data).map((field) => {
      const optionMap = data[field].options || {};
      const options = Object.keys(optionMap).map(key => ({ value: key, label: optionMap[key] }));
      return [field, options];
    });
    return Object.fromEntries(entries);
  }

  function makeCustomFieldsFromTapdFieldsInfoResult(data) {
    if (!data) {
      return [];
    }
    const res = Object.entries(data)
      .filter(([k, v]) => k.startsWith('custom_field_') && v)
      .map(([k, v]) => {
        // v.options的格式是：{ k1: k1, k2: k2 }，需要转换成[{ value: k1, label: k1 }, { value: k2, label: k2}]
        const options = Object.values(v.options || {}).map(x => ({ value: x, label: x }));
        return {
          customField: k,
          type: v.html_type,
          name: v.label,
          options,
        }
      });
    return res;
  }

  async function fetchTapdFieldsInfoAndSetState() {
    const nowMillis = +new Date();
    if (tapdFieldsInfoLastFetchedMillis + TAPD_API_CACHE_INTERVAL_MILLIS > nowMillis) {
      return;
    }
    try {
      const rsp = await RestHelper.post('/redir/query/data/queryTapdFieldsInfo', {
        appId,
        platformId,
      }, { silentFailure: true });
      if (!rsp.json || !rsp.json.data) {
        setTapdFieldsError({
          errorCode: (rsp.json || {}).errorCode,
          message: (rsp.json || {}).message,
        });
        return;
      }
      setTapdFieldsError(null);
      const data = rsp.json.data;
      setIterationList(makeSingleFieldOptionsFromTapdFieldsInfoResult(data, 'iteration_id'));
      setReleaseList(makeSingleFieldOptionsFromTapdFieldsInfoResult(data, 'release_id'));
      setVersionList(makeSingleFieldOptionsFromTapdFieldsInfoResult(data, 'version_report'));
      setTapdModuleList(makeSingleFieldOptionsFromTapdFieldsInfoResult(data, 'module'));
      setPriorityList(makeSingleFieldOptionsFromTapdFieldsInfoResult(data, 'priority'));
      setBugFieldValueToOptions(makeBugFieldValueToOptions(data));
      setBugCustomFields(makeCustomFieldsFromTapdFieldsInfoResult(data));
      setBugFieldToInfo(data);
      setTapdFieldsInfoLastFetchedMillis(nowMillis);
    } catch (e) {
      const { errorCode, message } = getTapdErrorFromReject(e);
      setTapdFieldsError({ errorCode, message });
    }
  }

  // 拉取TAPD项目人员列表并setState
  async function queryWorkspaceUsers() {
    const nowMillis = +new Date();
    if (workspaceUsersLastFetchedMillis + TAPD_API_CACHE_INTERVAL_MILLIS > nowMillis) {
      return;
    }
    const rsp = await RestHelper.post('/redir/query/data/queryWorkspaceUsers', {
      appId,
      platformId,
    });
    isArray(rsp.json.data) && setWorkspaceUserOptions(rsp.json.data.map(x => ({
      value: x.user,
      label: x.user,
    })));
    setWorkspaceUsersLastFetchedMillis(nowMillis);
  }

  // 拉取项目下的TAPD缺陷模板列表，默认选中TAPD侧标记为default的模板。
  // 拉取失败不阻断提单（降级为“不使用模板”，沿用tapdFieldList固定字段方案），
  // 但要通过notifyTapdFailure让用户感知到失败（不能真的静默，否则会被误以为“本来就没有模板”）。
  async function fetchTapdBugTemplateListAndSetState() {
    try {
      const rsp = await RestHelper.post('/redir/query/data/queryTapdBugTemplateList', {
        appId,
        platformId,
      }, { silentFailure: true });
      if (!modalVisibleRef.current) { // 请求返回前弹窗已经关了，别再 setState
        return;
      }
      const list = isArray(rsp.json && rsp.json.data) ? rsp.json.data : [];
      setTapdTemplateList(list);
      // 项目配了「默认提单模板」就按配置取初始值（__NONE__=不使用模板，指定模板=该模板）；
      // 未配置或「跟随TAPD默认」时，取TAPD侧 default=1 的模板（改造前的行为）。
      const { templateId, invalidSetting } = resolveInitialTemplateId(tapdBugDefaultTemplateSetting, list);
      setSelectedTemplateId(templateId);
      setDefaultTemplateIdFromSetting(templateId);
      // 列表为空（项目没配模板）时不提示：那不是「配置的模板失效」，只是没有模板可用
      if (invalidSetting && list.length > 0) {
        notification.warning({
          key: 'tapd-default-template-invalid',
          message: ze('配置的默认提单模板已不存在', 'The configured default template no longer exists'),
          description: ze('TAPD 侧已找不到该模板，本次已回退为 TAPD 项目默认模板。', 'The template can no longer be found in TAPD; fell back to the TAPD project default template this time.'),
        });
      }
    } catch (e) {
      // 拉取失败不阻断提单（模板下拉为空，退化为不使用模板），但必须让用户感知到失败，
      // 不能真的什么提示都没有，否则用户会以为“这个项目本来就没有模板”。
      const { errorCode, message } = getTapdErrorFromReject(e);
      notifyTapdFailure(errorCode, message, ze('TAPD 模板列表获取失败', 'Failed to load TAPD template list'));
    }
  }

  // 选中模板变化时，拉取该模板的字段配置（哪些字段/是否必填/默认值/排序）
  useEffect(() => {
    if (!selectedTemplateId) {
      setTemplateFieldConfigList(null);
      setTemplateFieldsLoading(false);
      templateFieldConfigOwnerRef.current = '';
      return;
    }
    let cancelled = false;
    setTemplateFieldsLoading(true);
    // 开始拉新模板：先把「配置属于哪个模板」清空，避免下面 reconcile 拿着上一个模板的字段配置跑一轮
    templateFieldConfigOwnerRef.current = '';
    (async () => {
      try {
        const rsp = await RestHelper.post('/redir/query/data/queryTapdBugTemplateFields', {
          appId,
          platformId,
          templateId: selectedTemplateId,
        }, { silentFailure: true });
        if (cancelled) {
          return;
        }
        const list = isArray(rsp.json && rsp.json.data) ? rsp.json.data : [];
        // 这个结果（哪怕是空/失败降级）属于当前模板，标记后 reconcile 才处理
        templateFieldConfigOwnerRef.current = selectedTemplateId;
        // 注意：不能落成[]。[]是truthy，会让下面templateEnabledFieldList变成[]（而不是null），
        // 进而effectiveEnabledFieldList也变成[]（[] || x 取的是[]），导致字段全部消失。
        // 拉取失败/返回异常数据/模板确实没有配置字段时，都统一降级为“不按模板过滤”（沿用tapdFieldList）。
        setTemplateFieldConfigList(list.length > 0 ? list : null);
      } catch (e) {
        if (!cancelled) {
          setTemplateFieldConfigList(null);
          // 失败也算「这个模板的配置已确认」，让 reconcile 走降级后的字段集合
          templateFieldConfigOwnerRef.current = selectedTemplateId;
          // 同上，拉取失败要让用户感知到（表单会退化成不按模板过滤，但不能悄无声息）。
          const { errorCode, message } = getTapdErrorFromReject(e);
          notifyTapdFailure(errorCode, message, ze('TAPD 模板字段配置获取失败', 'Failed to load TAPD template field config'));
        }
      } finally {
        if (!cancelled) {
          setTemplateFieldsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTemplateId, appId, platformId]);

  const [resetFormTrigger, setResetFormTrigger] = useState(0);
  useEffect(() => {
    if (!resetFormTrigger) {
      return;
    }
    formInstance.resetFields();
  }, [resetFormTrigger]);
  function onSaveBugCustomFields() {
    setTapdEditBugCustomFieldsModalVisible(false);
    const initialForm = makeInitialPartialForm(issueInfo, isMultipleIssues); // 保存设定后，重置表单（不保留用户填写字段）
    updateForm(initialForm);
    setResetFormTrigger(resetFormTrigger + 1);
  }

  async function fetchAttachments() {
    const { detailInfo } = issueInfo;
    if (!detailInfo) {
      notification.error({ message: ze('附件信息获取失败。无法获取Crash ID','Failed to obtain attachment information. Unable to obtain Crash ID') });
      return;
    }
    const { crashId } = detailInfo;
    setAttachmentOptionsLoading(true);
    const rsp = await RestHelper.get(`/appDetailCrash/appId/${appId}/platformId/${platformId}/crashHash/${crashId}`);
    const filenameWhitelist = [
      ...CrashAttachmentUtil.getKnownAttachmentFilenames(),
      'userExtraByteData',
      'crash_attach.log',
    ];
    const uniqueFilenames = uniq((rsp.json.ret.attachList || [])
      .map(x => x.fileName)
      .filter(x => filenameWhitelist.includes(x)));
    const filenameOptions = uniqueFilenames.map(x => ({ label: CrashAttachmentUtil.getFileAliasByFilename(x), value: x }));
    setAttachmentFilenameOptions(filenameOptions);
    formInstance.setFieldsValue({ attachmentFilenameList: filenameOptions.map((options) => options.value) });
    updateForm(draft => ({ ...draft, attachmentFilenameList: filenameOptions.map((options) => options.value) }));
    setAttachmentOptionsLoading(false);
    setAttachmentOptionsLoaded(true);
  }

  // 拉取迭代信息
  useEffect(() => {
    if (!modalVisible) {
      return;
    }
    if(!bondedJiraProject){
      fetchJiraId();
    }
    if (tapdWorkspaceId) {
      fetchTapdFieldsInfoAndSetState();
      fetchTapdBugTemplateListAndSetState();
    }
    queryWorkspaceUsers();
  }, [modalVisible]);

  // 打开modal的时候，设置内部用的issue信息，并设置form触发器
  useEffect(() => {
    (async () => {
      if (!modalVisible) {
        return;
      }
      if (!issueInfoList || !issueInfoList.length) {
        notification.error({ message: ze('未拉取到bug info','Bug info not fetched') });
        console.error(ze('打开tapdmodal的时候未设置issueInfoList','IssueInfoList is not set when opening tapdmodal'));
      }

      let newIssueInfo = issueInfoList[0];
      updateIssueInfo((draft) => {
        Object.assign(draft, newIssueInfo);
      });
      // 如果在issue列表那边提bug，issueInfoList里面是没有detailInfo（最近一次crash的信息）的，需要拉一下
      if (!isMultipleIssues && !newIssueInfo.detailInfo) {
        setLoading(true);
        const issueId = newIssueInfo.issueId;
        const rsp = await RestHelper.mustPost('/api/crash/fetchLastCrashDocInIssue', { appId, platformId, issueId });
        updateIssueInfo((draft) => {
          draft.detailInfo = CrashUtil.makeRefinedCrashMap(rsp.json.data.crashMap);
        });
        setLoading(false);
      }
      // 王者需求：需要填充AI分析结论
      setAiSolutionMarkdownText('');
      if (isZh() && EnvUtil.isChinaEnv() && !isMultipleIssues) {
        const issueId = newIssueInfo.issueId;
        const rsp = await RestHelper.mustPost('/api/aiPilot/getLatestAiMessage', {
          appId,
          issueId,
        });
        const latestMessageObj = rsp.json.data?.data?.[0];
        let answer = latestMessageObj?.answer;
        if (answer) {
          answer = AiIssueAnalysisUtil.convertAnswerToMarkdownText(answer);
          const { rootCauseText } = IssueAiSolutionUtil.parseResult(answer, IssueAiSolutionItemType.ROOT_CAUSE);
          const { markdownText: suggestionText } = IssueAiSolutionUtil.parseResult(answer, IssueAiSolutionItemType.SUGGESTION);
          setAiSolutionMarkdownText(`### 根因结论：\n\n${rootCauseText}\n\n### 修复建议：\n\n${suggestionText}\n`);
        }
      }
      setFormUpdateTrigger(formUpdateTrigger + 1);
    })();
  }, [modalVisible]);

  // modal显示状态变化时的处理
  useEffect(() => {
    if (modalVisible) {
      // 打开modal时，如果jira字段已经加载过（jiraBugTypeList有值），直接设置为已加载
      if (bondedJiraProject && jiraBugTypeList && jiraBugTypeList.length > 0) {
        setJiraFieldsLoaded(true);
      }
    } else {
      // 关闭modal时，重置部分state
      setTabType(TAB_CREATE_BUG);
      setAttachmentFilenameOptions([]);
      setAttachmentOptionsLoaded(false);
      setJiraFieldsLoaded(false);
      // 模板选择不跨issue保留，下次打开重新按项目默认模板走一遍
      setSelectedTemplateId('');
      setDefaultTemplateIdFromSetting('');
      // 关外层弹窗时一并关内层（antd 嵌套 Modal 不会自动关）
      setTapdDefaultTemplateModalVisible(false);
      setTemplateFieldConfigList(null);
      setTemplateFieldsLoading(false);
      previousTemplateFieldNamesRef.current = null;
      previousTemplateIdForReconcileRef.current = '';
      templateFieldConfigOwnerRef.current = '';
      autoFilledKeysRef.current = new Set();
    }
  }, [modalVisible]);

  useEffect(() => {
    if (!formUpdateTrigger) { // skip first render
      return;
    }

    // 只替换部分字段，保留部分填写过的字段
    updateForm((draft) => {
      const newDraft = {
        ...draft,
        ...makeInitialPartialForm(issueInfo, isMultipleIssues),
      };
      // draft里面的object类型属性被proxy代理了，直接给setFieldsValue会抛异常，需要deepClone一下去掉proxy
      formInstance.setFieldsValue(cloneDeep(newDraft));
      return newDraft;
    });
  }, [formUpdateTrigger]);

  // 表单“是否包含附件”切换到true的时候，拉取附件列表
  useEffect(() => {
    if (!form.includeAttachments || attachmentOptionsLoaded) {
      return;
    }
    fetchAttachments();
  }, [form.includeAttachments]);

  // 当标题模板修改的时候，修改form的标题
  useEffect(() => {
    const enrichedIssueInfo = { ...issueInfo, platformId };
    updateForm((draft) => {
      const newDraft = {
        ...draft,
        title: TapdTitleTemplateUtil.makeTitleFromTemplate(titleTemplateFromSettings, enrichedIssueInfo, enrichedIssueInfo?.detailInfo),
      };
      // draft里面的object类型属性被proxy代理了，直接给setFieldsValue会抛异常，需要deepClone一下去掉proxy
      formInstance.setFieldsValue(cloneDeep(newDraft));
      return newDraft;
    });
  }, [titleTemplateFromSettings]);

  // 当Jira字段加载完成时，应用默认值
  useEffect(() => {
    if (!jiraFieldsLoaded || !modalVisible) {
      return;
    }

    // 应用默认值
    if (jiraFieldDefaultValue && Object.keys(jiraFieldDefaultValue).length > 0) {
      const defaultValues = {};

      // 遍历字段列表，如果defaultValue中有对应的值，则设置
      jiraBugTypeList.forEach(field => {
        const fieldKey = field.key;
        if (isNotNullish(jiraFieldDefaultValue[fieldKey])) {
          defaultValues[fieldKey] = jiraFieldDefaultValue[fieldKey];
        }
      });

      // 设置表单值
      if (Object.keys(defaultValues).length > 0) {
        formInstance.setFieldsValue(defaultValues);
        updateForm(draft => {
          Object.assign(draft, defaultValues);
        });
      }
    }
  }, [jiraFieldsLoaded, modalVisible]);

  function jiraBugTypeItem(data) {
    return data.map(item => {
      return {
        value: item.id,
        label: item.value || item.name,
      }
    })
  }

  // 保存Jira字段的默认值
  function saveJiraFieldDefaultValue() {
    // 从当前表单值中提取Jira字段的值
    const jiraFieldValues = {};

    // 遍历jiraBugTypeList，从form中提取对应的值
    jiraBugTypeList.forEach(field => {
      const fieldKey = field.key;
      const fieldValue = form[fieldKey];

      // 只保存有值的字段
      if (isNotNullish(fieldValue)) {
        jiraFieldValues[fieldKey] = fieldValue;
      }
    });

    // 也保存issueType
    if (isNotNullish(form.issueType)) {
      jiraFieldValues.issueType = form.issueType;
    }

    // 保存到ServerAppSettings
    dispatch(upsertServerAppSettings(appId, {
      [ServerAppSettings.keys.jiraFieldDefaultValue]: jiraFieldValues,
    }));

    notification.success({ message: ze('字段模板保存成功', 'Template saved successfully') });
  }

  /**
   * 将驼峰式字段名转换为下划线式字段名并去重
   *
   * 背景：
   * - 旧版代码中，部分字段使用驼峰式命名（如 versionReport, iterationId）
   * - API 返回的字段使用下划线式命名（如 version_report, iteration_id）
   * - 为了统一，配置保存时使用下划线式，但需要兼容旧配置
   *
   * enabledFieldList 的来源：
   * - 如果有新配置（ServerAppSettings.tapdFieldList），已经是下划线式或驼峰式混合
   * - 如果没有新配置，使用默认配置（驼峰式）
   *
   * 处理逻辑：
   * 1. 将所有驼峰式字段名转换为下划线式（通过 CAMEL_TO_SNAKE_CASE_FIELD_MAP）
   * 2. 去重（防止驼峰式和下划线式同时存在）
   * 3. 返回统一的下划线式字段列表
   */
  const normalizedEnabledFieldList = useMemo(() => {
    const fieldSet = new Set();
    const result = [];

    enabledFieldList.forEach(item => {
      // 将驼峰式字段名转换为下划线式
      const snakeCaseFieldName = CAMEL_TO_SNAKE_CASE_FIELD_MAP[item.field] || item.field;

      // 去重
      if (!fieldSet.has(snakeCaseFieldName)) {
        fieldSet.add(snakeCaseFieldName);
        result.push({
          field: snakeCaseFieldName,
          required: item.required,
        });
      }
    });

    return result;
  }, [enabledFieldList]);

  /**
   * 选了TAPD缺陷模板时，字段集合完全由模板决定（覆盖tapdFieldList的勾选结果），
   * 必填/默认值也来自模板配置；没选模板（selectedTemplateId为空）时为null，走原来tapdFieldList的逻辑。
   */
  const templateEnabledFieldList = useMemo(() => {
    if (!selectedTemplateId || !templateFieldConfigList) {
      return null;
    }
    return withAlwaysRequiredTemplateFields(templateFieldConfigList, defaultReporter)
      .filter(x => !!x.field && !FEATURE_ANALYSIS_SKIP_TEMPLATE_FIELDS[x.field])
      .sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0))
      .map(x => ({
        field: x.field,
        required: x.required === '1',
        defaultValue: x.value,
      }));
  }, [selectedTemplateId, templateFieldConfigList, defaultReporter]);

  const effectiveEnabledFieldList = templateEnabledFieldList || normalizedEnabledFieldList;

  // 两种触发都要处理（缺一都会让模板默认值进不来）：
  // 1) 字段集合变化（首次应用模板 / 模板字段配置到达）：保留共有字段，清掉旧模板专属 key；
  // 2) 用户主动切了模板下拉（templateId 变化）：即使两个模板字段集合完全相同，也要按新模板重填默认值。
  // 两者都不发生时不动表单，避免打断用户正在填写的内容。
  // --story=1020421727138452632
  useEffect(() => {
    if (!modalVisible) {
      return;
    }
    // 选了模板但该模板的字段配置还没到（还在拉，或属于上一个模板）→ 等，不消耗基线，
    // 否则会先按旧模板的默认值 reconcile 一轮，新模板的默认值就永远填不进来。
    if (selectedTemplateId && templateFieldConfigOwnerRef.current !== selectedTemplateId) {
      return;
    }
    const currentFieldNames = effectiveEnabledFieldList.map(x => x.field);
    const previousFieldNames = previousTemplateFieldNamesRef.current;
    if (previousFieldNames === null) { // 首次渲染只记基线，不做处理（避免开弹窗就误清空）
      previousTemplateFieldNamesRef.current = currentFieldNames;
      previousTemplateIdForReconcileRef.current = selectedTemplateId;
      return;
    }
    const templateIdChanged = previousTemplateIdForReconcileRef.current !== selectedTemplateId;
    const fieldSetChanged = previousFieldNames.join(',') !== currentFieldNames.join(',');
    if (!templateIdChanged && !fieldSetChanged) {
      return;
    }
    previousTemplateFieldNamesRef.current = currentFieldNames;
    previousTemplateIdForReconcileRef.current = selectedTemplateId;
    // 切模板时，上一份模板「自动填进去」的默认值也要一起清掉——TAPD 模板字段高度重合，
    // 不清的话切过去看到的还是上一个模板的默认值（用户手填过的字段不在 autoFilledKeysRef 里，不动）。
    const staleAutoFilledKeys = templateIdChanged
      ? [...autoFilledKeysRef.current].filter(
        // reporter 是模板之外强制补的必填项（模板里没有这个字段），切到「不使用模板」或降级时
        // 清掉它会导致必填项变空且没有默认值可回填，所以排除
        k => !TAPD_MODAL_RESERVED_FORM_KEYS.includes(k) && k !== TAPD_TEMPLATE_ALWAYS_REQUIRED_REPORTER,
      )
      : [];
    const gone = removedKeys(previousFieldNames, currentFieldNames, TAPD_MODAL_RESERVED_FORM_KEYS)
      .concat(staleAutoFilledKeys);
    // 先显式删掉 gone（含上一份模板自动填的值），再按新模板填默认值
    const cleared = deleteRemovedTemplateKeys(form, gone);
    const nextForm = applyTemplateDefaults(cleared, effectiveEnabledFieldList);
    // 记录这一轮由默认值写入/改写的 key；用户之后手填会在 onValuesChange 里从这个集合移除。
    // 基准取 cleared（清理之后），避免把「只是改了 key 名字」的值误判成自动填的值
    autoFilledKeysRef.current = new Set(
      Object.keys(nextForm).filter(
        k => !TAPD_MODAL_RESERVED_FORM_KEYS.includes(k)
          && k !== TAPD_TEMPLATE_ALWAYS_REQUIRED_REPORTER
          && nextForm[k] !== (cleared || {})[k],
      ),
    );
    updateForm(() => nextForm);
    ignoreTemplateReconcileRef.current = true;
    formInstance.setFieldsValue(antdClearAndSet(gone, nextForm));
    ignoreTemplateReconcileRef.current = false;
  }, [effectiveEnabledFieldList, modalVisible, selectedTemplateId]);

  const formTapdFieldsComponent = <div>
    {/* TAPD缺陷模板选择：选了模板后，字段集合、必填、默认值都按模板走 */}
    <Form.Item label={ze('提单模板', 'Template')}>
      <Select
        allowClear
        placeholder={ze('不使用模板', 'No template')}
        style={{ width: '100%' }}
        loading={templateFieldsLoading}
        options={tapdTemplateList.map(x => ({
          value: x.id,
          label: String(x.id) === String(defaultTemplateIdFromSetting) && defaultTemplateIdFromSetting
            ? `${x.name}${ze('（默认）', ' (Default)')}`
            : x.name,
        }))}
        value={selectedTemplateId || undefined}
        onChange={(value) => setSelectedTemplateId(value || '')}
      />
    </Form.Item>
    <div style={{ marginTop: '-8px', marginBottom: '4px' }}>
      <Tooltip
        mouseEnterDelay={0.3}
        title={!hasAdminPermission ? ze('仅项目管理员可操作', 'Only project admins can set the default template.') : undefined}
      ><Button
        type='link'
        disabled={!hasAdminPermission}
        onClick={() => setTapdDefaultTemplateModalVisible(true)}
      >{ ze('设置默认模板', 'Set Default Template') }{ !hasAdminPermission && ze('（无权限）', '(No Permission)') }</Button></Tooltip>
    </div>

    <Spin spinning={templateFieldsLoading}>
    <div style={{ minHeight: templateFieldsLoading ? 160 : undefined }}>
    {/* 渲染所有已配置的字段；loading 期间不画，避免改到上一份模板 */}
    { !templateFieldsLoading && effectiveEnabledFieldList.map(item => {
      const { field: snakeCaseFieldName, required } = item;

      // 从 bugFieldToInfo 获取字段信息
      const fieldInfo = bugFieldToInfo[snakeCaseFieldName];

      // 如果字段信息不存在，跳过
      if (!fieldInfo) {
        return null;
      }

      const label = fieldInfo.label || snakeCaseFieldName;

      return (
        <TapdFieldFormItemErrorBoundary key={snakeCaseFieldName}>
          <Form.Item
            label={label}
            name={snakeCaseFieldName}
            rules={required ? [{ required: true, message: `请填写${label}` }] : undefined}
          >
            <TapdFieldFormItem
              fieldInfo={fieldInfo}
              reporterOptions={reporterOptions}
              workspaceUserOptions={workspaceUserOptions}
            />
          </Form.Item>
        </TapdFieldFormItemErrorBoundary>
      );
    }) }

    {/* 设置字段按钮：已选模板时字段集合由模板决定，这里的设置不生效，禁用并提示 */}
    <div>
      <Tooltip
        mouseEnterDelay={0.3}
        title={
          selectedTemplateId
            ? ze('已选择提单模板，字段由模板决定，这里的设置暂不生效', 'A template is selected; fields are determined by the template and this setting has no effect.')
            : (!hasAdminPermission ? ze('仅项目管理员可操作', 'Only project admins can set fields.') : undefined)
        }
      ><Button
        type='link'
        disabled={!hasAdminPermission || !!selectedTemplateId}
        onClick={() => {
          setTapdEditBugCustomFieldsModalVisible(true);
        }}
      >{ ze('设置TAPD字段', 'Set TAPD Fields') }{ !hasAdminPermission && ze('（无权限）', '(No Permission)') }</Button>
      </Tooltip>
    </div>
    </div>
    </Spin>
  </div>;

  const formJiraFieldsComponent = <div style={{height:'514px', overflow:'auto'}}>
    <Form.Item
      label={ze('Jira项目', 'Jira Project')}
      name='JiraProject'
    >
      {bondedJiraProject && <a href={bondedJiraProject.baseUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>{`${bondedJiraProject.name} (id:${bondedJiraProject.jiraProjectId}, key:${bondedJiraProject.jiraProjectKey})`}</a>}
    </Form.Item>
    <Form.Item
      label={ze('问题类型', 'Issue Type')}
      name='issueType'
      rules={[{ required: true }]}
    >
      <Select
        options={jiraTypesList}
        disabled={typesLoading}
        loading={typesLoading}
        onChange={(value) => getFieldOfJiraBugType(value)}
        onFocus={() => {
          if(jiraTypesList.length === 0)
            notification.error({message: ze('请在Jira项目中设置"问题"或"缺陷"问题类型。', 'Please set the "Issue" type in your Jira project.')})
        }} />
    </Form.Item>
    {jiraBugTypeList.map((item, index) => (
      <Form.Item
        label={item.name}
        name={item.key}
        rules={[{ required: item.required }]}
      >
        {(() => {
          if (isJiraTeamField(item)) {
            if (jiraTeamOptions.length === 0 && !hasAdminPermission) {
              // 非管理员 + 项目没配过选项：下拉里没有可选项、又改不了配置，
              // 而 Team 在 Jira 侧可能是必填的——这时会让提单完全无法进行，所以降级成手填 UUID
              return <Input
                placeholder={ze('请填写 Team UUID，可从 Jira 团队页 /people/team/<UUID> 获取', 'Enter Team UUID, from Jira team page /people/team/<UUID>')}
              />;
            }
            // Team 字段：选项来自项目配置（不与 Jira 打通，客户自行维护），value 是 Team UUID。
            // 即使一个选项都没配过也保持下拉形态——否则用户就没有入口打开「配置Team选项」了。
            return <Select
              showSearch
              allowClear
              optionFilterProp='label'
              options={toTeamSelectOptions(jiraTeamOptions)}
              placeholder={ze('请选择团队', 'Please select a team')}
              notFoundContent={ze('暂无可选团队，可在下方「配置Team选项」中添加', 'No team yet, add one via "Configure Team Options" below')}
              // 参考 VersionHybridSelect：配置入口放在下拉底部，而不是塞在表单字段列表最下方
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '4px 0' }} />
                  <div
                    style={{ padding: '4px 12px 8px' }}
                    // 阻止点击导致输入框失焦从而关闭下拉
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Button
                      type='link'
                      size='small'
                      disabled={!hasAdminPermission}
                      onClick={() => setJiraTeamOptionsModalVisible(true)}
                    >
                      { ze('配置Team选项', 'Configure Team Options') }
                      { !hasAdminPermission && ze('（无权限）', '(No Permission)') }
                    </Button>
                  </div>
                </>
              )}
            />;
          }
          if (item.restrict) {
            return <Select
              mode={item.multiple ? 'multiple' : null}
              showSearch
              allowClear
              optionFilterProp="label"
              options={jiraBugTypeItem(item.options)}
            />;
          } else if (item.type === 'number') {
            return <InputNumber
              style={{ width: '100%' }}
            />;
          } else {
            return <Input
              placeholder={item.multiple ? ze('多个标签请用逗号分隔','Please separate multiple tags with commas') : ''}
            />;
          }
        })()}
      </Form.Item>
    ))}
    {jiraFieldsLoaded && <div>
      <Tooltip
        mouseEnterDelay={0.3}
        title={!hasAdminPermission ? ze('仅项目管理员可操作', 'Only project admins can save templates.') : undefined}
      >
        <Popconfirm
          title={ze('确认要保存当前填写的字段值到模板吗？下次提单时将自动填充模板值到字段中。', 'Save current field values as template? The template values will be automatically filled when creating issues next time.')}
          onConfirm={saveJiraFieldDefaultValue}
          okText={ze('确认', 'Confirm')}
          cancelText={ze('取消', 'Cancel')}
          disabled={!hasAdminPermission}
        >
          <Button
            type='link'
            disabled={!hasAdminPermission}
          >
            { ze('保存字段值为模板', 'Save as Template') }{ !hasAdminPermission && ze('（无权限）', '(No Permission)') }
          </Button>
        </Popconfirm>
      </Tooltip>
    </div>}
    { jiraTeamOptionsModalVisible && <JiraTeamOptionsModal
      visible={jiraTeamOptionsModalVisible}
      appId={appId}
      teamFieldKey={jiraTeamFieldKey}
      value={jiraTeamOptionsText}
      jiraFieldDefaultValue={jiraFieldDefaultValue}
      onCancel={() => setJiraTeamOptionsModalVisible(false)}
      onSaved={({ defaultUuid }) => {
        setJiraTeamOptionsModalVisible(false);
        if (!jiraTeamFieldKey) { return; }
        // 用户已经手填/手选过 Team 就不要覆盖。判定用 autoFilledKeysRef：
        // 该集合记录「由默认值自动填进去」的字段，用户在 onValuesChange 里改动过就会被移除，
        // 比"当前值 == 旧默认值"准（用户手选的值恰好等于旧默认值时不会被误判）。
        const currentValue = form[jiraTeamFieldKey];
        const untouchedByUser = isNullish(currentValue)
          || currentValue === ''
          || autoFilledKeysRef.current.has(jiraTeamFieldKey);
        if (!untouchedByUser) { return; }
        // 保存后立即生效：否则要关掉重开弹窗，默认值才会填进去
        if (defaultUuid) {
          formInstance.setFieldsValue({ [jiraTeamFieldKey]: defaultUuid });
          updateForm(draft => { draft[jiraTeamFieldKey] = defaultUuid; });
        } else {
          // 未设默认 / 清空配置：清掉残留值，避免提交一个下拉里已经不存在的 UUID
          formInstance.setFieldsValue({ [jiraTeamFieldKey]: undefined });
          updateForm(draft => { delete draft[jiraTeamFieldKey]; });
        }
      }}
    /> }
  </div>;

  const tapdFieldsErrorAlert = tapdFieldsError && (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message={formatTapdErrorMessage(
        tapdFieldsError.errorCode,
        tapdFieldsError.message,
        ze('TAPD 字段信息获取失败', 'Failed to load TAPD field info'),
      )}
      description={shouldShowTapdConfigLink(tapdFieldsError.errorCode, tapdFieldsError.message) ? (
        <a onClick={() => redirectToTapdConfigPage()}>
          {ze('前往缺陷管理平台配置', 'Go to Bug Tracking Platform Settings')}
        </a>
      ) : undefined}
    />
  );

  const formTapdComponent = (
    tabType === TAB_CREATE_BUG && <div className={scss.formContainer}>
      {tapdFieldsErrorAlert}
      <Form
      layout='vertical'
      form={formInstance}
      initialValues={form}
      onValuesChange={(changedValues) => {
        if (ignoreTemplateReconcileRef.current) {
          return;
        }
        // 用户自己动过的字段不再算「模板自动填的默认值」，切模板时不能被清掉重填
        Object.keys(changedValues || {}).forEach(k => autoFilledKeysRef.current.delete(k));
        updateForm((draft) => mergeFormChange(draft, changedValues));
      }}
      onFinish={() => {
        if (tabType === TAB_CREATE_BUG) {
          onSubmitBugFormAfterValidate();
        }
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ flex: 'auto' }}>
          {!isMultipleIssues && <Form.Item
            label={t('tapd.缺陷标题')}
            help={(() => {
              const titleLength = form.title ? form.title.length : 0;
              return titleLength > MAX_TITLE_LENGTH
                ? <div style={{ color: red.primary }}>{ze(
                  `标题已超过字符长度限制（当前 ${titleLength}，限制 ${MAX_TITLE_LENGTH} ），超出部分将被截断`,
                  `The title has exceeded the character length limit (currently ${titleLength}, limit ${MAX_TITLE_LENGTH}), and the excess part will be truncated.`
                )}</div>
                : null;
            })()}
          >
            <Row gutter={0} >
              <Col ><Form.Item
                name='title'
                noStyle
                rules={[{ required: true }]}
              >
                <Input style={{width: '470px'}} />
              </Form.Item></Col>
              <Col><Button
                type='link'
                style={{marginLeft: '10px', width: 'fit-content'}}
                onClick={() => setTapdTitleTemplateModalVisible(true)}
              >{ ze('设置标题模板', 'Edit Title Template') }</Button></Col>
            </Row>
          </Form.Item>}

          {!isMultipleIssues && <Form.Item
            label={t('tapd.缺陷详情')}
            name='description'
            rules={[{ required: true }]}
          >
            <TextArea style={{width: '600px', height: '360px'}} />
          </Form.Item>}
          {!isMultipleIssues && !!aiSolutionMarkdownText && <div style={{margin: '24px 0px'}}>
            <span style={{marginRight: '10px'}}>{t('tapd.包含AI分析结论')}</span>
            <Form.Item
              name='includeAiSolution'
              noStyle={true}
            >
              <Radio.Group>
                <Radio value={false}>{t('tapd.不包含')}</Radio>
                <Radio value={true}>{t('tapd.包含')}</Radio>
              </Radio.Group>
            </Form.Item></div>}
          {!isMultipleIssues && <div style={{margin: '24px 0px'}}>
            <span style={{marginRight: '10px'}}>{t('tapd.包含附件下载链接')}</span>
            <Form.Item
              name='includeAttachments'
              noStyle={true}
            >
            <Radio.Group>
              <Radio value={false}>{t('tapd.不包含')}</Radio>
              <Radio value={true}>{t('tapd.包含')}</Radio>
            </Radio.Group>
          </Form.Item></div>}
          {!isMultipleIssues && form.includeAttachments &&
            <Spin spinning={attachmentOptionsLoading}>
              <span style={{marginRight: '10px'}}>{t('tapd.选择要包含的附件')}</span>
              <Form.Item name='attachmentFilenameList' noStyle={true}>
                <Checkbox.Group options={attachmentFilenameOptions}/>
              </Form.Item>
            </Spin>}

          {isMultipleIssues && <Form.Item
            label={t('tapd.缺陷标题')}
          >
            <Row gutter={0}>
              <Col ><Form.Item
                noStyle
                rules={[{ required: true }]}
              >
                <Input style={{width: '470px'}} disabled={true} />
              </Form.Item></Col>
              <Col><Button
                type='link'
                disabled={true}
                style={{marginLeft: '10px', width: 'fit-content'}}
              >{ ze('设置标题模板', 'Edit Title Template') }</Button></Col>
            </Row>
          </Form.Item>}
          {isMultipleIssues && <Form.Item
            label={t('tapd.缺陷详情')}
            rules={[{ required: true }]}
          >
            <TextArea style={{width: '600px', height: '360px'}} disabled={true}/>
          </Form.Item>}
          {isMultipleIssues && <div style={{margin: '24px 0px'}}>
            <span style={{marginRight: '10px'}}>{t('tapd.包含附件下载链接')}</span>
            <Form.Item
              name='includeAttachments'
              noStyle={true}
            >
            <Radio.Group disabled={true}>
              <Radio value={false}>{t('tapd.不包含')}</Radio>
              <Radio value={true}>{t('tapd.包含')}</Radio>
            </Radio.Group>
          </Form.Item></div>}
          {isMultipleIssues && form.includeAttachments &&
            <Spin spinning={attachmentOptionsLoading} disabled={true}>
              <span style={{marginRight: '10px'}}>{t('tapd.选择要包含的附件')}</span>
              <Form.Item name='attachmentFilenameList' noStyle={true}>
                <Checkbox.Group options={attachmentFilenameOptions}/>
              </Form.Item>
            </Spin>}
        </div>
        <Divider type='vertical'  style={{ height: 'auto', margin: '0 20px' }}/>
        <div style={{ width: '240px' }}>
          { formTapdFieldsComponent }
        </div>
      </div>
    </Form></div>
  );

  const formJiraComponent = (
    tabType === TAB_CREATE_BUG && <div className={scss.formContainer}><Form
      layout='vertical'
      form={formInstance}
      initialValues={form}
      onValuesChange={(changedValues) => {
        if (ignoreTemplateReconcileRef.current) {
          return;
        }
        updateForm((draft) => mergeFormChange(draft, changedValues));
      }}
      onFinish={() => {
        if (tabType === TAB_CREATE_BUG) {
          onSubmitJiraBugForm();
        }
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ flex: 'auto' }}>
          {!isMultipleIssues && <Form.Item
            label={t('tapd.缺陷标题')}
            help={(() => {
              const titleLength = form.title ? form.title.length : 0;
              return titleLength > MAX_TITLE_LENGTH
                ? <div style={{ color: red.primary }}>{ze(
                  `标题已超过字符长度限制（当前 ${titleLength}，限制 ${MAX_TITLE_LENGTH} ），超出部分将被截断`,
                  `The title has exceeded the character length limit (currently ${titleLength}, limit ${MAX_TITLE_LENGTH}), and the excess part will be truncated.`
                )}</div>
                : null;
            })()}
          >
            <Row gutter={0}>
              <Col ><Form.Item
                name='title'
                noStyle
                rules={[{ required: true }]}
              >
                <Input style={{width: '470px'}} />
              </Form.Item></Col>
              <Col><Button
                type='link'
                style={{marginLeft: '10px', width: 'fit-content'}}
                onClick={() => setTapdTitleTemplateModalVisible(true)}
              >{ ze('设置标题模板', 'Edit Title Template') }</Button></Col>
            </Row>
          </Form.Item>}

          {!isMultipleIssues && <Form.Item
            label={t('tapd.缺陷详情')}
            name='description'
            rules={[{ required: true }]}
          >
            <TextArea style={{width: '600px', height: '360px'}} />
          </Form.Item>}
          {!isMultipleIssues && <div style={{margin: '24px 0px'}}>
            <span style={{marginRight: '10px'}}>{t('tapd.包含附件下载链接')}</span>
            <Form.Item
              name='includeAttachments'
              noStyle={true}
            >
            <Radio.Group>
              <Radio value={false}>{t('tapd.不包含')}</Radio>
              <Radio value={true}>{t('tapd.包含')}</Radio>
            </Radio.Group>
          </Form.Item></div>}
          {!isMultipleIssues && form.includeAttachments &&
            <Spin spinning={attachmentOptionsLoading}>
              <span style={{marginRight: '10px'}}>{t('tapd.选择要包含的附件')}</span>
              <Form.Item name='attachmentFilenameList' noStyle={true}>
                <Checkbox.Group options={attachmentFilenameOptions}/>
              </Form.Item>
            </Spin>}
        </div>
        <Divider type='vertical' style={{ height: 'auto', margin: '0 20px' }}/>
        <div style={{ width: '240px' }}>
          <Spin spinning={!jiraFieldsLoaded}>
            { formJiraFieldsComponent }
          </Spin>
        </div>
      </div>
    </Form></div>
  );

  // 在创建bug前，对表单数据做处理（比如超长的标题截断等）
  function refineCreateBugFormDataForSubmit() {
    const updateFields = {};
    if (form.title && form.title.length > MAX_TITLE_LENGTH) {
      updateFields.title = form.title.substring(0, MAX_TITLE_LENGTH);
    }
    // 注意：current_owner 等 user_chooser 字段已经在 TapdFieldFormItem 内部处理为分号分隔的字符串格式
    // 不需要在这里做额外转换

    if (Object.keys(updateFields).length > 0) {
      updateForm(draft => ({ ...draft, ...updateFields }));
    }
  }

  // 点击表单提交的回调。（创建bug和绑定bug公共回调）
  // 需要做一些表单内容预处理
  const onClickSubmitForm = () => {
    if (tabType === TAB_CREATE_BUG) {
      refineCreateBugFormDataForSubmit();
    }
    setFormSubmitTrigger(formSubmitTrigger + 1);
  };

  // 表单提交trigger effect。（创建bug和绑定bug公共）
  useEffect(() => {
    if (!formSubmitTrigger) {
      return;
    }
    if (tabType === TAB_CREATE_BUG) {
      formInstance.setFieldsValue(form);
    }
    formInstance.submit();
  }, [formSubmitTrigger]);

  /** 拉取附带的附件，并转换为URL等链接信息，用于添加到bug单正文里面 */
  async function makeExtraDescriptionFromAttachments() {
    let extraDescription = '';
    try {
      if (form.includeAttachments && form.attachmentFilenameList.length > 0) {
        const { issueId } = issueInfo;
        const detailInfo = issueInfo.detailInfo;
        const { crashId } = detailInfo;
        const equivalentUserRole = hasViewPersonalDataPermission ? UserTypeEnum.MEMBER : UserTypeEnum.STATS_VIEWER;
        const rsp = await RestHelper.post('/api/issue/generateReportShareLink', {
          appId,
          platformId,
          issueId,
          crashId,
          expireInSeconds: 365 * 86400, // 一年有效期
          equivalentUserRole,
        });
        const { shareId } = rsp.json.data;
        const downloadLinks = form.attachmentFilenameList.map(name => {
          const nameRemoveDot = name.replace(/\./g, 'dot');
          const url = `${window.location.protocol}//${window.location.host}/showAttachment/shareId/${shareId}/fileName/${nameRemoveDot}`;
          return `<a href='${url}' download='${CrashAttachmentUtil.getFileAliasByFilename(name)}'>${CrashAttachmentUtil.getFileAliasByFilename(name)}</a><br>`;
        }).join('');
        extraDescription = `<br><br>${ze('查看附件（链接有效期1年）','View attachment (link valid for 1 year)')}：<br>${downloadLinks}`;
      }
      return extraDescription;
    } catch (e) {
      notification.error({ message: ze('生成附件分享链接失败，请联系系统管理员。','Failed to generate attachment sharing link, please contact the administrator.') });
      throw e;
    }
  }

  const notifyTapdFailure = (errorCode, message, fallback) => {
    const text = formatTapdErrorMessage(errorCode, message, fallback);
    notification.error({
      message: text,
      description: shouldShowTapdConfigLink(errorCode, message) ? (
        <a onClick={() => redirectToTapdConfigPage()}>
          {ze('前往缺陷管理平台配置', 'Go to Bug Tracking Platform Settings')}
        </a>
      ) : undefined,
    });
  };

  async function onSubmitBugFormAfterValidate() {
    if (tabType !== TAB_CREATE_BUG) {
      return;
    }

    if (form.reporter) {
      pushReporter(form.reporter);
    }

    setLoading(true);
    try {
      // 带有附件，需要先把附件接口转为share link，然后追加到提单正文里去
      let extraDescription = await makeExtraDescriptionFromAttachments();
      const createFailedFallback = ze('缺陷创建失败！', 'Create failed!');

      if (!isMultipleIssues) {
        try {
          const rsp = await RestHelper.post('/api/issue/upsertBugs', {
            appId,
            platformId,
            issueList: [{
              issueHash: issueInfo.issueId,
              bugInfoList: [makeFinalTapdBugForSubmit(issueInfo, form, isMultipleIssues, extraDescription)],
            }],
          }, { silentFailure: true });
          const isSuccess = rsp.json && rsp.json.data && rsp.json.data[0];
          if (isSuccess) {
            notification.success({ message: ze('缺陷创建成功！', 'Create successfully!') });
          } else {
            notifyTapdFailure((rsp.json || {}).errorCode, (rsp.json || {}).message, createFailedFallback);
          }
        } catch (e) {
          const { errorCode, message } = getTapdErrorFromReject(e);
          notifyTapdFailure(errorCode, message, createFailedFallback);
        }
      } else {
        const failedIssueIdList = [];
        let lastTapdError = {};
        for (const info of issueInfoList) {
          const issueId = info.issueId;
          let rsp = await RestHelper.mustPost('/api/crash/fetchLastCrashDocInIssue', { appId, platformId, issueId });
          const detailInfo = CrashUtil.makeRefinedCrashMap(rsp.json.data.crashMap);
          try {
            rsp = await RestHelper.post('/api/issue/upsertBugs', {
              appId,
              platformId,
              issueList: [{
                issueHash: info.issueId,
                bugInfoList: [makeFinalTapdBugForSubmit({...info, detailInfo}, form, isMultipleIssues, extraDescription)],
              }],
            }, { silentFailure: true });
            const isSuccess = rsp.json && rsp.json.data && rsp.json.data[0];
            if (!isSuccess) {
              failedIssueIdList.push(issueId);
              lastTapdError = { errorCode: (rsp.json || {}).errorCode, message: (rsp.json || {}).message };
            }
          } catch (e) {
            failedIssueIdList.push(issueId);
            lastTapdError = getTapdErrorFromReject(e);
          }
        }
        if (failedIssueIdList.length === 0) {
          notification.success({ message: ze('缺陷创建成功！', 'Create successfully!') });
        } else {
          notifyTapdFailure(
            lastTapdError.errorCode,
            lastTapdError.message,
            ze(`缺陷 ${failedIssueIdList.join(', ')} 创建失败。`, `${failedIssueIdList.join(', ')} create failed!`),
          );
        }
      }
      modalProps.onOk && modalProps.onOk();
    } finally {
      setLoading(false);
    }
  };
  async function onSubmitJiraBugForm() {
    if (tabType !== TAB_CREATE_BUG) {
      return;
    }
    if (form.reporter) {
      pushReporter(form.reporter);
    }
    setLoading(true);
    // 带有附件，需要先把附件接口转为share link，然后追加到提单正文里去
    let extraDescription = '';
    try {
      if (form.includeAttachments && form.attachmentFilenameList.length > 0) {
        const { issueId } = issueInfo;
        const detailInfo = issueInfo.detailInfo;
        const { crashId } = detailInfo;
        const equivalentUserRole = hasViewPersonalDataPermission ? UserTypeEnum.MEMBER : UserTypeEnum.STATS_VIEWER;
        const rsp = await RestHelper.post('/api/issue/generateReportShareLink', {
          appId,
          platformId,
          issueId,
          crashId,
          expireInSeconds: 365 * 86400, // 一年有效期
          equivalentUserRole,
        });
        const { shareId } = rsp.json.data;
        const downloadLinks = form.attachmentFilenameList.map(name => {
          const nameRemoveDot = name.replace(/\./g, 'dot');
          const url = `${window.location.protocol}//${window.location.host}/showAttachment/shareId/${shareId}/fileName/${nameRemoveDot}`;
          return `<a href='${url}'>${name}</a><br>`;
        }).join('');
        extraDescription = `<br><br>查看附件（链接有效期1年）：<br>${downloadLinks}`;
      }
    } catch (e) {
      notification.error({ message: '生成附件分享链接失败，请联系系统管理员。' });
      throw e;
    }
    let jiraBug = {
      summary: form.title,
      description: form.description,
      project: 'BUG',
      issueType: form.issueType,
      jiraFiledKvList: [],
    }
    if (extraDescription) {
      jiraBug = {
        ...jiraBug,
        description: jiraBug.description + extraDescription,
      };
    }
    for (const key in form) {
      if (isNullish(form[key])) {
        continue;
      }
      const formField = jiraBugTypeList.find(item => item.key === key);
      if(formField) {
        let valuesData = [];
        if(formField.restrict) {
          if(formField.multiple){
            valuesData = (form[key]).map(item => ({id: item}));
          } else {
            valuesData = [{id: form[key]}];
          }
        } else {
          if(formField.multiple){
            valuesData = (form[key].split(',')).map(item => ({input: item}));
          } else {
            valuesData = [{input: form[key]}];
          }
        }
        (jiraBug.jiraFiledKvList).push({
          key: key,
          values: valuesData,
          multiple: formField.multiple,
          restrict: formField.restrict,
        })
      }
    }
    if (!isMultipleIssues) {
      const rsp = await RestHelper.post('/redir/query/jira/upsertJiraBugs', {
        appId,
        platformId,
        issueList: [{
          issueHash: issueInfo.issueId,
          bugInfoList: [jiraBug]
        }],
      });
      const isSuccess = rsp.json.data[0].result;
      if (isSuccess) {
        notification.success({ message: ze('缺陷创建成功！', 'Create successfully!')});
      } else {
        notification.error({ message: ze('缺陷创建失败！', 'Create failed!') + rsp.json.data[0].msg });
      }
    } else {
      notification.error({ message: ze('暂不支持批量创建Jira缺陷', 'Batch creation of Jira issues is not currently supported.') });
    }
    setLoading(false);
    modalProps.onOk && modalProps.onOk();
  }

  const onSubmitBindJiraBugForm = async () => {
    if (tabType !== TAB_BIND_BUG) {
      return;
    }
    setLoading(true);
    const rsp = await RestHelper.post('/redir/query/jira/bindJiraBug', {
      appId,
      platformId,
      issueHash: issueInfo.issueId,
      jiraBugKey: form.jiraBugKey
    });
    const isSuccess = rsp.json.data;
    if (isSuccess) {
      notification.success({ message: ze('缺陷绑定成功。','Issue binding successful') });
    } else {
      notification.error({ message: ze('缺陷绑定失败。','Issue binding failed') });
    }
    setLoading(false);
    modalProps.onOk && modalProps.onOk();
  };

  const onSubmitBindBugFormAfterValidate = async () => {
    if (tabType !== TAB_BIND_BUG) {
      return;
    }
    const addComment = !!form.isAlsoAddComment;
    let commentDescriptionBase64;
    let commentAuthor;
    if (addComment) {
      // 带有附件，需要先把附件接口转为share link，然后追加到提单正文里去
      let extraDescription = await makeExtraDescriptionFromAttachments();
      commentDescriptionBase64 = Base64.encodeAsString((form.description || '') + extraDescription);
      commentAuthor = defaultReporter;
    }

    setLoading(true);
    const bugId = TapdUtil.getBugIdFromUrl(form.url);
    const bindFailedFallback = ze('缺陷绑定失败。', 'Issue binding failed');
    try {
      const rsp = await RestHelper.post('/redir/query/data/bindBug', {
        appId,
        platformId,
        workspaceId,
        bugId,
        issueHash: issueInfo.issueId,
        addComment,
        commentDescriptionBase64,
        commentAuthor,
      }, { silentFailure: true });
      const isSuccess = rsp.json && rsp.json.data;
      if (isSuccess) {
        notification.success({ message: ze('缺陷绑定成功。', 'Issue binding successful') });
      } else {
        notifyTapdFailure((rsp.json || {}).errorCode, (rsp.json || {}).message, bindFailedFallback);
      }
    } catch (e) {
      const { errorCode, message } = getTapdErrorFromReject(e);
      notifyTapdFailure(errorCode, message, bindFailedFallback);
    } finally {
      setLoading(false);
      modalProps.onOk && modalProps.onOk();
    }
  };

  const bindTapdBugComponent = (
    workspaceId && tabType === TAB_BIND_BUG && <div className={scss.formContainer}><Form
      layout='vertical'
      form={formInstance}
      initialValues={{ url: '' }}
      onValuesChange={(changedValues) => {
        updateForm((draft) => {
          Object.assign(draft, changedValues);
        });
      }}
      onFinish={() => {
        if (tabType === TAB_BIND_BUG) {
          onSubmitBindBugFormAfterValidate();
        }
      }}
    >
      <Form.Item
        label={ze('TAPD缺陷页面地址', 'TAPD Issue Web Page URL')}
        name='url'
        rules={[{
          required: true,
          type: 'url',
        }, {
          validator: (_, value) => {
            if (isNotNullish(TapdUtil.getBugIdFromUrl(value))) {
              return Promise.resolve();
            }
            return Promise.reject(Error(ze('不是合法的TAPD缺陷地址','Not a valid TAPD defect address')));
          },
        }]}
      >
        <Input/>
      </Form.Item>
      {!isMultipleIssues && <Form.Item
        name='isAlsoAddComment'
        valuePropName="checked"
      >
        <Checkbox>{ ze('追加详情到Tapd缺陷单评论', 'Add Detail to Comment') }</Checkbox>
      </Form.Item>}
      {!isMultipleIssues && !!form.isAlsoAddComment && <Form.Item
        label={t('tapd.缺陷详情')}
        name='description'
      >
        <TextArea style={{ height: '360px' }} />
      </Form.Item>}
      {!isMultipleIssues && !!form.isAlsoAddComment && <div style={{margin: '24px 0px'}}>
        <span style={{marginRight: '10px'}}>{t('tapd.包含附件下载链接')}</span>
        <Form.Item
          name='includeAttachments'
          noStyle={true}
        >
          <Radio.Group>
            <Radio value={false}>{t('tapd.不包含')}</Radio>
            <Radio value={true}>{t('tapd.包含')}</Radio>
          </Radio.Group>
        </Form.Item></div>}
      {!isMultipleIssues && !!form.isAlsoAddComment && form.includeAttachments &&
        <Spin spinning={attachmentOptionsLoading}>
          <span style={{marginRight: '10px'}}>{t('tapd.选择要包含的附件')}</span>
          <Form.Item name='attachmentFilenameList' noStyle={true}>
            <Checkbox.Group options={attachmentFilenameOptions}/>
          </Form.Item>
        </Spin>}
    </Form></div>
  );

  const bindJiraBugComponent = (
    bondedJiraProject && tabType === TAB_BIND_BUG && <Form
      form={formInstance}
      initialValues={{ url: '' }}
      onValuesChange={(changedValues) => {
        updateForm((draft) => {
          Object.assign(draft, changedValues);
        });
      }}
      onFinish={() => {
        if (tabType === TAB_BIND_BUG) {
          onSubmitBindJiraBugForm();
        }
      }}
    >
      <Form.Item
        label={ze(`JiraBugKey(形如：${bondedJiraProject.jiraProjectKey}-1)`, `JiraBugKey(like：${bondedJiraProject.jiraProjectKey}-1)`)}
        name='jiraBugKey'
        rules={[{
          required: true,
        }, {
          validator: (_, value) => {
            const pattern = new RegExp('^' + bondedJiraProject.jiraProjectKey);
            if (pattern.test(value)) {
              return Promise.resolve();
            }
            return Promise.reject(Error('不是合法的JiraBugKey'));
          },
        }]}
      >
        <Input/>
      </Form.Item>
    </Form>
  );

  let finalModalProps;
  if (workspaceIdLoading || jiraIdLoading) {
    finalModalProps = {
      ...modalProps,
      title: t('tapd.缺陷管理平台绑定状态加载中'),
      okButtonProps: {
        disabled: true,
      },
    };
  } else if (workspaceId) {
    finalModalProps = {
      ...modalProps,
      title: isMultipleIssues
        ? ze(`批量提交${issueInfoList.length}个缺陷到TAPD`, `Bulk Submit ${issueInfoList.length} TAPD Issues`)
        : <Space>
          <div>{t('tapd.提交缺陷到TAPD')}</div>
          {!isMultipleIssues &&  <Select
                style={{ width: '200px' }}
                options={[
                  { label: t('tapd.创建新TAPD缺陷'), value: TAB_CREATE_BUG },
                  { label: t('tapd.关联已有TAPD缺陷'), value: TAB_BIND_BUG },
                ]}
                value={tabType}
                onChange={(value) => {
                  setTabType(value)
                }}
              />}
        </Space>,
      width: '960px',
      maskClosable: false,
      onOk: () => onClickSubmitForm(),
    };
  } else if (bondedJiraProject) {
    finalModalProps = {
      ...modalProps,
      title: isMultipleIssues
        ? `${ze('批量提交','Batch submit ')}${issueInfoList.length}${ze(' 个缺陷到Jira',' issues to Jira')}`
        : <Space>
          <div>{ze('提交缺陷到Jira', 'Submit issues to Jira')}</div>
          {!isMultipleIssues && <Radio.Group
            defaultValue={tabType}
            value={tabType}
            options={[
              { label: ze('创建新的Jira缺陷', 'Create New Issue'), value: TAB_CREATE_BUG },
              { label: ze('关联已有Jira缺陷', 'Bind Existed Issue'), value: TAB_BIND_BUG },
            ]}
            optionType='button'
            onChange={(v) => setTabType(v.target.value)}
          />}
        </Space>,
      width: '960px',
      maskClosable: false,
      onOk: () => onClickSubmitForm(),
    };
  } else {
    finalModalProps = {
      ...modalProps,
      title: ze('绑定缺陷管理系统', 'Integrate with an bug tracking system'),
      onOk: () => redirectToTapdConfigPage(),
    };
  }
  useEffect(() => {
    if(bondedJiraProject && bondedJiraProject.jiraProjectId){
      getAssignableUsers();
      getJiraTypes();
    }
  }, [bondedJiraProject])

  async function getAssignableUsers() {
    setUsersLoading(true);
    const rsp = await RestHelper.mustPost('/redir/query/jira/getAssignableUsers', { appId, platformId });
    if(rsp.json.data){
      const jiraUserList = rsp.json.data.map(item => {
        return{
          value: item.accountId,
          label: item.displayName
        }
      })
      setJiraUserList(jiraUserList);
    }
    else{
      notification.error({message: ze('Jira项目人员列表获取失败。', 'Failed to get user list for Jira project.')})
    }
    setUsersLoading(false);
  }

  async function getJiraTypes() {
    setTypesLoading(true);
    const rsp = await RestHelper.mustPost('/redir/query/jira/getJiraTypes', { appId, platformId });
    if(rsp.json.data){
      const jiraTypesList = rsp.json.data.map(item => {
        return{
          value: item.id,
          label: item.name
        }
      })
      // 优先使用默认值，如果没有则使用第一个
      const jiraBugTypeValue = jiraFieldDefaultValue?.issueType || jiraTypesList[0].value;
      formInstance.setFieldsValue({ issueType:jiraBugTypeValue });
      updateForm(draft => ({ ...draft, issueType:jiraBugTypeValue }));
      setJiraTypesList(jiraTypesList);
      getFieldOfJiraBugType(jiraBugTypeValue);
    }
    else{
      notification.error({message: ze('Jira项目问题类型获取失败。', 'Failed to get issue type for Jira project')})
    }
    setTypesLoading(false);
  }


  async function getFieldOfJiraBugType(jiraBugType) {
    const rsp = await RestHelper.mustPost('/redir/query/jira/getFieldOfJiraBugType',{
      appId,
      platformId,
      jiraBugType,
    });
    const data = rsp.json.data;
    setJiraBugTypeList(data);
    setJiraFieldsLoaded(true);
  }

  return (
    <Modal {...finalModalProps} className={scss.tapdStyle}>
      { workspaceIdLoading && jiraIdLoading && <Spin spinning={workspaceIdLoading && jiraIdLoading} style={{ width: '100%' }}/> }
      {/*Tapd*/}
      { !workspaceIdLoading && workspaceId && <Spin spinning={loading}>
        {/* !loading,!jiraIdLoading 是为了防止model宽度在计算的过程中内容已经渲染，导致内容超出model盒子 */}
        {tabType === TAB_CREATE_BUG && !loading && !jiraIdLoading && formTapdComponent}
        {tabType === TAB_BIND_BUG && bindTapdBugComponent}
      </Spin>}
      {/*Jira*/}
      { !jiraIdLoading && bondedJiraProject && <Spin spinning={loading}>
        {/* !loading,!jiraIdLoading 是为了防止model宽度在计算的过程中内容已经渲染，导致内容超出model盒子 */}
        {tabType === TAB_CREATE_BUG && !loading && !jiraIdLoading && formJiraComponent}
        {tabType === TAB_BIND_BUG && bindJiraBugComponent}
      </Spin>}
      { !workspaceIdLoading && !workspaceId && !jiraIdLoading && !bondedJiraProject && <div>
        <div>{ ze('项目尚未绑定缺陷管理系统，是否要前往“缺陷管理平台配置”页进行配置', 'This project has not been integrated with any bug tracking system, Would you like to go to the "Integrations" page to set it up?') }</div>
      </div>}
      { aiSolutionMarkdownText && <div style={{ display: 'none' }}><div ref={aiSolutionMarkdownRef}>
        <ReactMarkdown
          remarkPlugins={[gfm]}
          linkTarget='_blank'
          children={aiSolutionMarkdownText}
        />
      </div></div> }
      <TapdModelEditTitleTemplateModel
        visible={tapdTitleTemplateModalVisible}
        issueInfo={issueInfo}
        lastReportCrashMap={issueInfo?.detailInfo}
        onCancel={() => {
          setTapdTitleTemplateModalVisible(false);
        }}
        onSave={() => {
          setTapdTitleTemplateModalVisible(false);
        }}
      />
      <TapdEditBugCustomFieldsModal
        visible={tapdEditBugCustomFieldsModalVisible}
        bugFieldInfoList={bugFieldInfoList}
        onCancel={() => setTapdEditBugCustomFieldsModalVisible(false)}
        onSave={() => onSaveBugCustomFields()}
      />
      <TapdDefaultTemplateModal
        visible={tapdDefaultTemplateModalVisible}
        appId={appId}
        settingKey={ServerAppSettings.keys.tapdBugDefaultTemplate}
        settingValue={tapdBugDefaultTemplateSetting}
        templateList={tapdTemplateList}
        onCancel={() => setTapdDefaultTemplateModalVisible(false)}
        onSaved={(value) => {
          // 只更新「（默认）」标记，不改写当前表单已选的模板：
          // 改 selectedTemplateId 会触发切模板清理，把用户已经填好的字段清掉。新值下次打开表单时生效。
          setDefaultTemplateIdFromSetting(resolveInitialTemplateId(value, tapdTemplateList).templateId);
          setTapdDefaultTemplateModalVisible(false);
        }}
      />
    </Modal>
  );
};

TapdModal.propTypes = {
  issueInfoList: PropTypes.Array,
  modalProps: PropTypes.object,
  reduxState: PropTypes.object,
};

const mapStateToProps = state => ({
  reduxState: state,
});

export default connect(mapStateToProps)(TapdModal);
